// HistoryViewer.tsx
import React, { useState, useMemo } from 'react';
import ToastManager from '../../../Components/ToastManager';

interface HistoryViewerProps {
    historyData: any[];
    onClose: () => void;
}

interface VersionItem {
    id: string;
    traceId: number;
    version: number;
    label: string;
    data: any;
    creationDate: string;
    lastUpdatedBy?: string;
    comments: string;
    selected: boolean;
    originalIndex: number;
}

interface DiffResult {
    path: string;
    type: 'added' | 'removed' | 'changed';
    leftValue?: any;
    rightValue?: any;
}

// Custom diff function to replace deep-diff
const getDiff = (left: any, right: any, path: string = ''): DiffResult[] => {
    const diffs: DiffResult[] = [];

    // Handle primitive types
    if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) {
        if (left !== right) {
            diffs.push({
                path: path || 'root',
                type: 'changed',
                leftValue: left,
                rightValue: right
            });
        }
        return diffs;
    }

    // Handle arrays
    if (Array.isArray(left) || Array.isArray(right)) {
        const leftArray = Array.isArray(left) ? left : [];
        const rightArray = Array.isArray(right) ? right : [];
        const maxLen = Math.max(leftArray.length, rightArray.length);

        for (let i = 0; i < maxLen; i++) {
            const currentPath = path ? `${path}[${i}]` : `[${i}]`;
            if (i >= leftArray.length) {
                diffs.push({
                    path: currentPath,
                    type: 'added',
                    rightValue: rightArray[i]
                });
            } else if (i >= rightArray.length) {
                diffs.push({
                    path: currentPath,
                    type: 'removed',
                    leftValue: leftArray[i]
                });
            } else {
                diffs.push(...getDiff(leftArray[i], rightArray[i], currentPath));
            }
        }
        return diffs;
    }

    // Handle objects
    const allKeys: any = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
    
    for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;
        const leftValue = left?.[key];
        const rightValue = right?.[key];

        if (!(key in left)) {
            diffs.push({
                path: currentPath,
                type: 'added',
                rightValue: rightValue
            });
        } else if (!(key in right)) {
            diffs.push({
                path: currentPath,
                type: 'removed',
                leftValue: leftValue
            });
        } else {
            diffs.push(...getDiff(leftValue, rightValue, currentPath));
        }
    }

    return diffs;
};

const HistoryViewer: React.FC<HistoryViewerProps> = ({ historyData, onClose }) => {
    const [versions, setVersions] = useState<VersionItem[]>([]);
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);

    // Process history data into versions
    useMemo(() => {
        if (!historyData || historyData.length === 0) {
            setVersions([]);
            return;
        }

        const processedVersions: VersionItem[] = [];
        
        // Sort by traceId ascending (oldest first)
        const sortedData = [...historyData].sort((a, b) => a.traceId - b.traceId);
        
        let versionCounter = 0;
        
        // Process each history entry in order (oldest to newest)
        for (let i = 0; i < sortedData.length; i++) {
            const item = sortedData[i];
            
            // For the first entry (oldest traceId)
            // payload = initial state (Version 0)
            // newPayload = first change (Version 1)
            if (i === 0) {
                // Add payload as first version (initial state)
                if (item.payload && Object.keys(item.payload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-payload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.payload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.createdBy || item.lastUpdatedBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
                
                // Add newPayload as next version
                if (item.newPayload && Object.keys(item.newPayload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-newPayload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.newPayload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.lastUpdatedBy || item.createdBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
            } else {
                // For subsequent entries, only add newPayload
                // (payload equals previous newPayload, so it's already added)
                if (item.newPayload && Object.keys(item.newPayload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-newPayload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.newPayload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.lastUpdatedBy || item.createdBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
            }
        }

        // If no versions were created from payloads, create versions from newPayload only
        if (processedVersions.length === 0) {
            for (let i = 0; i < sortedData.length; i++) {
                const item = sortedData[i];
                if (item.newPayload && Object.keys(item.newPayload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-newPayload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.newPayload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.lastUpdatedBy || item.createdBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
            }
        }

        // Now processedVersions is in ascending order (oldest to newest)
        // Version numbers should be: oldest = 0, newest = length - 1
        
        // Keep them in ascending order (oldest first, newest last)
        // This means Version 0 is the oldest, Version N is the latest
        processedVersions.forEach((v, idx) => {
            v.version = idx;
            v.label = `Version ${idx}`;
        });

        // Mark the last version as latest
        if (processedVersions.length > 0) {
            const lastIndex = processedVersions.length - 1;
            processedVersions[lastIndex] = {
                ...processedVersions[lastIndex],
                label: `Version ${lastIndex}`
            };
        }

        setVersions(processedVersions);
        
        // Auto-select the last two versions (latest and previous)
        // This shows the most recent changes by default
        if (processedVersions.length >= 2) {
            const lastIndex = processedVersions.length - 1;
            const updated = processedVersions.map((v, idx) => ({
                ...v,
                selected: idx === lastIndex || idx === lastIndex - 1
            }));
            setVersions(updated);
            setSelectedVersions(updated.filter(v => v.selected).map(v => v.id));
        } else if (processedVersions.length === 1) {
            const updated = processedVersions.map((v) => ({
                ...v,
                selected: true
            }));
            setVersions(updated);
            setSelectedVersions(updated.filter(v => v.selected).map(v => v.id));
        }
    }, [historyData]);

    const handleVersionSelection = (versionId: string) => {
        const currentSelected = versions.filter(v => v.selected);
        const version = versions.find(v => v.id === versionId);
        
        if (!version) return;

        // If clicking on already selected version, deselect it
        if (version.selected) {
            const updatedVersions = versions.map(v => 
                v.id === versionId ? { ...v, selected: false } : v
            );
            setVersions(updatedVersions);
            setSelectedVersions(updatedVersions.filter(v => v.selected).map(v => v.id));
            return;
        }

        // Check if we already have 2 selected
        if (currentSelected.length >= 2) {
            ToastManager.showToast("Only 2 versions can be compared at once", 'Warning');
            return;
        }

        // Select the version
        const updatedVersions = versions.map(v => 
            v.id === versionId ? { ...v, selected: true } : v
        );
        setVersions(updatedVersions);
        setSelectedVersions(updatedVersions.filter(v => v.selected).map(v => v.id));
    };

    const handleClearAll = () => {
        const updatedVersions = versions.map(v => ({
            ...v,
            selected: false
        }));
        setVersions(updatedVersions);
        setSelectedVersions([]);
    };

    const getSelectedItems = () => {
        return versions.filter(v => v.selected);
    };

    const renderInlineHighlightedJSON = (data: any, diffs: DiffResult[], isLeft: boolean) => {
        const jsonStr = JSON.stringify(data, null, 2);
        
        if (diffs.length === 0) {
            return <pre className="p-2 mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStr}</pre>;
        }

        const lines = jsonStr.split('\n');
        const lineDiffMap: Map<number, { type: string, diff: DiffResult }> = new Map();
        
        lines.forEach((line, lineIndex) => {
            const trimmedLine = line.trim();
            const keyMatch = trimmedLine.match(/^"([^"]+)":/);
            const arrayMatch = trimmedLine.match(/^"(\d+)":/);
            const key = keyMatch ? keyMatch[1] : arrayMatch ? `[${arrayMatch[1]}]` : null;
            
            if (key) {
                for (const diff of diffs) {
                    const pathParts = diff.path.split(/[\.\[\]]/).filter(p => p !== '');
                    const lastPart = pathParts[pathParts.length - 1];
                    
                    if (lastPart === key || key.includes(lastPart) || lastPart.includes(key)) {
                        lineDiffMap.set(lineIndex, {
                            type: diff.type === 'added' ? (isLeft ? 'added' : 'added') :
                                  diff.type === 'removed' ? (isLeft ? 'removed' : 'removed') :
                                  'changed',
                            diff: diff
                        });
                        break;
                    }
                }
            }
        });

        if (lineDiffMap.size === 0) {
            lines.forEach((line, lineIndex) => {
                for (const diff of diffs) {
                    const pathParts = diff.path.split(/[\.\[\]]/).filter(p => p !== '');
                    const lastPart = pathParts[pathParts.length - 1];
                    
                    if (line.includes(`"${lastPart}"`) || line.includes(lastPart)) {
                        lineDiffMap.set(lineIndex, {
                            type: diff.type === 'added' ? (isLeft ? 'added' : 'added') :
                                  diff.type === 'removed' ? (isLeft ? 'removed' : 'removed') :
                                  'changed',
                            diff: diff
                        });
                        break;
                    }
                }
            });
        }

        return (
            <div className="p-2 mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {lines.map((line, index) => {
                    const diffInfo = lineDiffMap.get(index);
                    if (diffInfo) {
                        let bgColor = 'bg-warning bg-opacity-25';
                        let borderColor = 'border-warning';
                        if (diffInfo.type === 'added') {
                            bgColor = 'bg-success bg-opacity-25';
                            borderColor = 'border-success';
                        } else if (diffInfo.type === 'removed') {
                            bgColor = 'bg-danger bg-opacity-25';
                            borderColor = 'border-danger';
                        }
                        return (
                            <div 
                                key={index} 
                                className={`${bgColor} p-1 my-0 ${borderColor} border-start border-3`}
                                title={`Diff: ${diffInfo.diff.path} (${diffInfo.type})`}
                            >
                                <span style={{ whiteSpace: 'pre' }}>{line}</span>
                            </div>
                        );
                    }
                    return <div key={index} style={{ whiteSpace: 'pre' }}>{line}</div>;
                })}
            </div>
        );
    };

    const renderSideBySideComparison = (version1: VersionItem, version2: VersionItem) => {
        const diffs = getDiff(version1.data, version2.data);
        const hasDifferences = diffs.length > 0;

        return (
            <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
                    <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold">Comparing:</span>
                        <span className="text-primary">{version1.label}</span>
                        <span className="mx-1">↔</span>
                        <span className="text-success">{version2.label}</span>
                    </div>
                    {!hasDifferences && (
                        <span className="badge bg-success">✅ No differences found</span>
                    )}
                    {hasDifferences && (
                        <span className="badge bg-warning text-dark">⚠️ {diffs.length} difference(s) found</span>
                    )}
                </div>
                {hasDifferences ? (
                    <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
                        <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #dee2e6' }}>
                            <div className="bg-primary bg-opacity-10 p-2 border-bottom sticky-top">
                                <strong className="text-primary">{version1.label}</strong>
                                <span className="text-muted ms-2 small">{version1.creationDate}</span>
                            </div>
                            {renderInlineHighlightedJSON(version1.data, diffs, true)}
                        </div>
                        <div style={{ flex: 1, overflow: 'auto' }}>
                            <div className="bg-success bg-opacity-10 p-2 border-bottom sticky-top">
                                <strong className="text-success">{version2.label}</strong>
                                <span className="text-muted ms-2 small">{version2.creationDate}</span>
                            </div>
                            {renderInlineHighlightedJSON(version2.data, diffs, false)}
                        </div>
                    </div>
                ) : (
                    <div className="text-center p-5">
                        <i className="fa-regular fa-check-circle fa-3x text-success mb-3 d-block"></i>
                        <h5 className="text-success">No differences found</h5>
                        <p className="text-muted">The two versions are identical</p>
                    </div>
                )}
            </div>
        );
    };

    const renderComparison = () => {
        const selectedItems = getSelectedItems();

        if (selectedItems.length === 0) {
            return (
                <div className="text-center p-5 text-muted">
                    <i className="fa-regular fa-circle-check fa-2x mb-3 d-block"></i>
                    <p>Select up to 2 versions to compare</p>
                    <small className="text-muted">Click on checkboxes below to select versions</small>
                </div>
            );
        }

        if (selectedItems.length === 1) {
            return (
                <div style={{ flex: 1, height: '100%', overflow: 'auto' }}>
                    <div className="bg-light p-2 border-bottom">
                        <strong>{selectedItems[0].label}</strong>
                        <span className="text-muted ms-2 small">{selectedItems[0].creationDate}</span>
                    </div>
                    <div className="p-2">
                        <pre className="p-2 mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(selectedItems[0].data, null, 2)}
                        </pre>
                    </div>
                </div>
            );
        }

        const [version1, version2] = selectedItems;
        return renderSideBySideComparison(version1, version2);
    };

    if (!versions || versions.length === 0) {
        return <div className="text-center p-5 font-italic">No History Found</div>;
    }

    const selectedCount = versions.filter(v => v.selected).length;

    return (
        <div style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: '0 0 15%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Versions</strong>
                        {selectedCount > 0 && (
                            <span className="badge bg-primary ms-2">{selectedCount} selected</span>
                        )}                        
                        {selectedCount >= 2 && (<small className="text-danger ms-2">Only 2 versions can be compared at once. Clear selection to choose different versions.</small>)}
                    </div>
                    <div className="d-flex gap-2">
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleClearAll}
                        >
                            Clear All
                        </button>
                    </div>
                </div> */}
                <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', padding: '5px', display: 'flex', gap: '15px' }}>
                    {versions.map((version, index) => {
                        const isSelected = version.selected;
                        const isDisabled = !isSelected && selectedCount >= 2;
                        const isLatest = index === versions.length - 1;

                        return (
                            <div 
                                key={version.id}
                                className={`p-2 border rounded ${isSelected ? 'border-primary bg-light' : 'border-secondary'}`}
                                style={{ 
                                    minWidth: '250px', 
                                    maxWidth: '300px', 
                                    flex: '0 0 auto',
                                    opacity: isDisabled ? 0.6 : 1
                                }}
                            >
                                <div className="d-flex align-items-start gap-3">
                                    <input 
                                        type="checkbox" 
                                        role={isDisabled ? "button" : undefined}
                                        className="form-check-input mt-1"
                                        checked={isSelected}
                                        onChange={() => handleVersionSelection(version.id)}
                                        disabled={isDisabled}
                                        title={isDisabled ? 'Only 2 versions can be compared at once' : 'Select version for comparison'}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div className="d-flex justify-content-between align-items-end">
                                            <div className="fw-bold">
                                                {version.label}
                                            </div>
                                            <div>
                                            {isLatest && (
                                               <small className="ms-2 mb-2"><span className="badge bg-success">Latest</span></small>
                                            )}
                                            {isSelected && (
                                                <small className="ms-1 mb-1"><span className="badge bg-primary">Selected</span></small>
                                            )}
                                            </div>
                                        </div>
                                        <div className="text-muted small mt-1">
                                            <i className="fa-regular fa-calendar me-1"></i>
                                            {version.creationDate}
                                        </div>
                                        <div className="text-muted small mt-1">
                                            <i className="fa-regular fa-user me-1"></i>
                                            {version?.lastUpdatedBy}
                                        </div>
                                        {version.comments && (
                                            <div className="text-muted small mt-1" title={version.comments}>
                                                <i className="fa-regular fa-comment me-1"></i>
                                                {version.comments.length > 30 ? 
                                                    version.comments.substring(0, 30) + '...' : 
                                                    version.comments}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div style={{ flex: '0 0 85%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderBottom: '1px solid #dee2e6' }}>
                <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
                    {renderComparison()}
                </div>
            </div>
        </div>
    );
};

export default HistoryViewer;